/** Debug overlay showing per-enemy skill state: active skill, phase, timers, stamina, and cooldowns. */
import Phaser from 'phaser';
import { Enemy } from '../../entities/Enemy';
import { crispText } from '../../ui/CrispUtil';
import { TextStyle, DEBUG_WORLD_ALPHA } from '../../ui/Theme';

const SKILL_LABEL_OFFSET_Y = -42;
const STAMINA_BAR_WIDTH = 24;
const STAMINA_BAR_HEIGHT = 2;
const STAMINA_BAR_OFFSET_Y = -6;
const STAMINA_BG_COLOR = 0x222222;
const STAMINA_FILL_COLOR = 0x3399ff;

const PHASE_COLORS: Record<string, string> = {
  idle: '#666666',
  windup: '#ffcc00',
  active: '#ff4444',
  recovery: '#4488ff',
  cooldown: '#666666',
};

/** Pooled per-enemy skill debug info: active skill, phase, timers, stamina, and cooldowns. Reuses Text objects to avoid GPU texture churn. */
export class EnemySkillOverlay {
  private scene: Phaser.Scene;
  private pool: Phaser.GameObjects.Text[] = [];
  private activeCount = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Update pooled skill labels above each enemy: active skill + phase, stamina bar, cooldown indicators. */
  draw(graphics: Phaser.GameObjects.Graphics, enemies: readonly Enemy[]): void {
    let idx = 0;

    for (const enemy of enemies) {
      const registry = enemy.ai.skillRegistry;
      const active = registry.activeSkill;

      // Skill label: "SKILL_ID (PHASE 123ms)" or "idle"
      let text: string;
      let color: string;
      if (active && active.isActive?.()) {
        const remaining = Math.round(active.phaseTimeRemainingMs);
        text = `${active.skillId} (${active.phase} ${remaining}ms)`;
        color = PHASE_COLORS[active.phase] ?? '#aaaaaa';
      } else {
        // Show cooldowns of non-idle skills
        const cds: string[] = [];
        for (const [id, skill] of registry.allSkills()) {
          if (skill.phase === 'cooldown') {
            cds.push(`${id}:${Math.round(skill.phaseTimeRemainingMs)}`);
          }
        }
        text = cds.length > 0 ? `cd: ${cds.join(' ')}` : 'idle';
        color = '#666666';
      }

      const label = this.getOrCreate(idx);
      if (label.text !== text) label.setText(text);
      label.setPosition(enemy.sprite.x, enemy.sprite.y + SKILL_LABEL_OFFSET_Y);
      if (label.style.color !== color) label.setColor(color);
      label.setVisible(true);
      idx++;

      // Stamina bar (below HP/aggro bars)
      const stamina = registry.stamina;
      if (stamina) {
        const x = enemy.sprite.x - STAMINA_BAR_WIDTH / 2;
        const y = enemy.sprite.y + STAMINA_BAR_OFFSET_Y;
        const ratio = stamina.current / stamina.max;

        graphics.fillStyle(STAMINA_BG_COLOR, 0.8);
        graphics.fillRect(x, y, STAMINA_BAR_WIDTH, STAMINA_BAR_HEIGHT);
        graphics.fillStyle(STAMINA_FILL_COLOR, 0.9);
        graphics.fillRect(x, y, STAMINA_BAR_WIDTH * ratio, STAMINA_BAR_HEIGHT);
      }
    }

    for (let i = idx; i < this.activeCount; i++) {
      this.pool[i].setVisible(false);
    }
    this.activeCount = idx;
  }

  private getOrCreate(index: number): Phaser.GameObjects.Text {
    if (index < this.pool.length) return this.pool[index];
    const label = crispText(this.scene, 0, 0, '', TextStyle.debugWorld)
      .setOrigin(0.5).setDepth(2001).setAlpha(DEBUG_WORLD_ALPHA);
    this.pool.push(label);
    return label;
  }

  /** Hide all active labels. */
  clear(): void {
    for (let i = 0; i < this.activeCount; i++) {
      this.pool[i].setVisible(false);
    }
    this.activeCount = 0;
  }

  /** Destroy all pooled labels and free resources. */
  destroy(): void {
    for (const label of this.pool) label.destroy();
    this.pool = [];
    this.activeCount = 0;
  }
}
